<script setup lang="ts">
import { FieldInput } from '@proj-airi/ui'
import { inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { RadioCardDetail } from '../../../../menu'
import { Button } from '../../../../misc'

const emit = defineEmits<Emits>()

const contentReady = inject<import('vue').Ref<boolean>>('onboarding-content-ready', ref(true))

interface Emits {
  (e: 'next', data: { name: string, avatar: string, gender: 'male' | 'female' | 'unspecified', birthday: string, persona: string }): void
  (e: 'skip'): void
}

const { t } = useI18n()

const defaultAvatar = '/USER.jpg'
const userName = ref('')
const userAvatar = ref(defaultAvatar)
const userGender = ref<'male' | 'female' | 'unspecified'>('unspecified')
const userBirthday = ref('')
const userPersona = ref('')
const birthdayInputRef = ref<HTMLInputElement>()
const fileInputRef = ref<HTMLInputElement>()

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const MAX_SIZE = 256

const genderOptions = [
  { id: 'male', label: () => t('settings.dialogs.onboarding.persona.genderMale') },
  { id: 'female', label: () => t('settings.dialogs.onboarding.persona.genderFemale') },
  { id: 'unspecified', label: () => t('settings.dialogs.onboarding.persona.genderUnspecified') },
]

function triggerFileSelect() {
  fileInputRef.value?.click()
}

function handleFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file)
    return
  if (!ALLOWED_TYPES.includes(file.type))
    return
  compressAndSetAvatar(file)
  input.value = ''
}

function compressAndSetAvatar(file: File) {
  const reader = new FileReader()
  reader.onload = () => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = MAX_SIZE
      canvas.height = MAX_SIZE
      const ctx = canvas.getContext('2d')!
      const size = Math.min(img.width, img.height)
      const sx = (img.width - size) / 2
      const sy = (img.height - size) / 2
      ctx.drawImage(img, sx, sy, size, size, 0, 0, MAX_SIZE, MAX_SIZE)
      userAvatar.value = canvas.toDataURL('image/jpeg', 0.85)
    }
    img.src = reader.result as string
  }
  reader.readAsDataURL(file)
}

function handleNext() {
  emit('next', {
    name: userName.value.trim(),
    avatar: userAvatar.value,
    gender: userGender.value,
    birthday: userBirthday.value,
    persona: userPersona.value.trim(),
  })
}
</script>

<template>
  <div class="min-h-0 flex flex-1 flex-col gap-4">
    <template v-if="contentReady">
      <div
        v-motion
        :initial="{ opacity: 0, y: 16 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="380"
        class="flex flex-shrink-0 items-center justify-between"
      >
        <div>
          <h2 class="text-xl text-neutral-800 md:text-2xl dark:text-neutral-100">
            {{ t('settings.dialogs.onboarding.persona.title') }}
          </h2>
          <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {{ t('settings.dialogs.onboarding.persona.description') }}
          </p>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto space-y-4">
        <div
          v-motion
          :initial="{ opacity: 0, scale: 0.85 }"
          :enter="{ opacity: 1, scale: 1 }"
          :duration="400"
          :delay="100"
          class="flex flex-col items-center gap-2"
        >
          <div class="group relative cursor-pointer" @click="triggerFileSelect">
            <img
              :src="userAvatar"
              class="h-20 w-20 rounded-full object-cover shadow-lg ring-3 ring-primary-200/50 dark:ring-primary-800/50"
              alt="头像"
            >
            <div class="absolute bottom-0 right-0 h-7 w-7 flex items-center justify-center rounded-full bg-primary-500 shadow-md">
              <div class="i-solar:camera-bold h-4 w-4 text-white" />
            </div>
          </div>
          <button class="text-xs text-primary-600 dark:text-primary-400" @click="triggerFileSelect">
            {{ t('settings.dialogs.onboarding.persona.avatarChange') }}
          </button>
          <input
            ref="fileInputRef"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            class="hidden"
            @change="handleFileChange"
          >
        </div>

        <div
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="380"
          :delay="180"
          class="space-y-4"
        >
          <FieldInput
            v-model="userName"
            :label="t('settings.dialogs.onboarding.persona.nameLabel')"
            :placeholder="t('settings.dialogs.onboarding.persona.namePlaceholder')"
            type="text"
          />

          <div>
            <label class="mb-2 block text-sm text-neutral-700 font-medium dark:text-neutral-300">
              {{ t('settings.dialogs.onboarding.persona.genderLabel') }}
            </label>
            <div class="grid grid-cols-3 gap-2">
              <RadioCardDetail
                v-for="option in genderOptions"
                :id="option.id"
                :key="option.id"
                v-model="userGender"
                name="gender-selection"
                :value="option.id"
                :title="option.label()"
                :show-expand-collapse="false"
              />
            </div>
          </div>

          <div>
            <label class="mb-1 block text-sm text-neutral-700 font-medium dark:text-neutral-300">
              {{ t('settings.dialogs.onboarding.persona.birthdayLabel') }}
            </label>
            <input
              ref="birthdayInputRef"
              v-model="userBirthday"
              type="date"
              class="w-full border border-neutral-200 rounded-lg bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 focus:border-primary-500 dark:bg-neutral-800"
              :placeholder="t('settings.dialogs.onboarding.persona.birthdayPlaceholder')"
              @click="birthdayInputRef?.showPicker?.()"
            >
          </div>

          <div>
            <label class="mb-1 block text-sm text-neutral-700 font-medium dark:text-neutral-300">
              {{ t('settings.dialogs.onboarding.persona.personaLabel') }}
            </label>
            <textarea
              v-model="userPersona"
              class="min-h-20 w-full resize-none border border-neutral-200 rounded-lg bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 focus:border-primary-500 dark:bg-neutral-800"
              :placeholder="t('settings.dialogs.onboarding.persona.personaPlaceholder')"
              rows="3"
            />
          </div>
        </div>
      </div>

      <div
        v-motion
        :initial="{ opacity: 0 }"
        :enter="{ opacity: 1 }"
        :duration="300"
        :delay="300"
        class="flex gap-2"
      >
        <button
          class="flex-shrink-0 px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
          @click="emit('skip')"
        >
          {{ t('settings.dialogs.onboarding.skipStep') }}
        </button>
        <Button
          class="flex-1"
          :label="t('settings.dialogs.onboarding.next')"
          :disabled="!userName.trim()"
          @click="handleNext"
        />
      </div>
    </template>
  </div>
</template>
